import React, { useState } from 'react';
import { Input } from 'antd';

import { normalizeId } from '../../utils/ids';
import { handTagIdsOf, ownPrivateTagIdsOf } from '../../utils/itemTags';

// The item actions' pure helpers and the delete confirm's body, kept apart from
// ItemActionsMenu.js (KPR-1547, under the file-size ratchet of KPR-1478).
// ItemActionsMenu re-exports the two its callers import from it.

/**
 * Where "Duplicate" lands: the new-item form for the same card, seeded with
 * this item's element values through navigation state. No tags: they are
 * retired (docs/plans/tags.md §4.1), and a copy carries none. It is the form
 * that applies utils/duplicateCopy.js copyableOnDuplicate (never the account link, a
 * driven or numbered element, or one marked "Leave blank when duplicating"),
 * against the card's full merged definition.
 */
export const duplicateItemNavigation = (collectionId, cardId, item) => {
	// Its tags too (T9): the hand tags, and the person's own private tags on
	// it. The form asks the API which of them land in the copy, shows them
	// before Save, and says what it left off (ItemForm).
	const tagIds = handTagIdsOf(item);
	const privateTagIds = ownPrivateTagIdsOf(item);
	// Its files (PR 10b): a file element's ids ride with the values (the API
	// binds each to the copy, on the same bytes, when it is created), and the
	// form shows them from THIS item's thumbnails — so its id and its `files`
	// facts go along. A file element left blank on duplicating gets none.
	const files = item?.files && typeof item.files === 'object' && Object.keys(item.files).length ? item.files : null;
	const itemId = normalizeId(item?._id);
	return [
		`/collections/${collectionId}/items/new/${cardId}`,
		{
			state: {
				prefill: {
					elements: { ...(item?.elements || {}) },
					...(tagIds.length ? { tagIds } : {}),
					...(privateTagIds.length ? { privateTagIds } : {}),
					...(files && itemId ? { fileSource: { itemId, files } } : {}),
				},
			},
		},
	];
};

/**
 * The item delete confirmation's sentence (delete audit F15). The delete is a
 * HARD delete that takes the item's relationships with it — the old
 * "will be removed from this collection" read like a move — and it all comes
 * back together: from the item's History, or from the collection's Recently
 * deleted (a manager's list), for 30 days (keepr-api KPR-658 ends a deleted
 * record's History then, and Files.DELETED_FILE_GRACE_DAYS its attachments).
 */
export function itemDeleteConfirmText(attachmentCount = 0) {
	const n = Number(attachmentCount) || 0;
	if (!n) return 'The item is deleted with its links to other items. You can restore all of it from History or Recently deleted for 30 days.';
	return `The item is deleted with its links to other items and its ${n === 1 ? 'attachment' : `${n} attachments`}. You can restore all of it from History or Recently deleted for 30 days.`;
}

/**
 * The delete confirm's body: the sentence, and — on a card whose record
 * policy deletes only with a reason (`record.delete: 'manage-with-reason'`) —
 * the reason, which rides on the delete (delete audit F10: the web used to
 * send none, so such a record could not be deleted from the UI at all).
 * Its state is its own, so the dialog's later content update (the
 * attachment count) keeps what was typed.
 */
export function ItemDeleteBody({ text, reasonRequired, onReason }) {
	const [reason, setReason] = useState('');
	return (
		<>
			<p>{text}</p>
			{reasonRequired ? (
				<>
					<p>This card keeps its entries as records: a delete needs a reason, kept in the item's history.</p>
					<Input.TextArea
						aria-label='Reason for deleting'
						placeholder='Why is this record being deleted?'
						autoSize={{ minRows: 2, maxRows: 5 }}
						maxLength={2000}
						value={reason}
						onChange={event => { setReason(event.target.value); onReason(event.target.value); }}
					/>
				</>
			) : null}
		</>
	);
}
